import { toSportSlug } from './sports';
import type { ContestView } from './contest';

export type PortalMode = 'player' | 'coach';

export const getPortalBase = (mode: PortalMode = 'player'): string =>
  mode === 'coach' ? '/coach' : '/player';

export const getPortalPaths = (mode: PortalMode = 'player') => {
  const base = getPortalBase(mode);
  return {    dashboard: `${base}/dashboard`,
    profile: `${base}/profile`,
    learningCourses: `${base}/learning/courses`,
    learningRoadmap: `${base}/learning/roadmap`,
    learningSport: (sport: string) => `${base}/learning/courses/${sport.toLowerCase()}`,
    rankingOverview: `${base}/ranking-overview`,
    rankingEvaluation: (
      categoryId: string,
      params?: { sport?: string; level?: string; subcategory?: string; eval?: string }
    ) => {
      const qs = new URLSearchParams();
      if (params?.sport) qs.set('sport', params.sport);
      if (params?.level) qs.set('level', params.level);
      const contentSubcategories = new Set(['strokes-shots', 'shooting', 'drills', 'fitness']);
      if (params?.eval && contentSubcategories.has(categoryId)) {
        const query = qs.toString();
        return `${base}/ranking-evaluation/${categoryId}/${params.eval}${query ? `?${query}` : ''}`;
      }
      if (params?.subcategory) qs.set('subcategory', params.subcategory);
      if (params?.eval) qs.set('eval', params.eval);
      const query = qs.toString();
      return `${base}/ranking-evaluation/${categoryId}${query ? `?${query}` : ''}`;
    },
    performance: `${base}/performance`,
    performanceAssessment: (sport: string, assessmentType: string) =>
      `${base}/performance/${sport}/${assessmentType}`,
    performanceDefault: `${base}/performance/Badminton/skill-assessment-beginner-self`,
    reports: mode === 'coach' ? `${base}/reports` : `${base}/my-reports`,
    recommendations: `${base}/recommendations`,
    compare: mode === 'coach' ? `${base}/compare-students` : `${base}/compare-peers`,
    certificates: `${base}/certificates`,
    rankingReport: (reportId: string) => `${base}/ranking-report/${reportId}`,
    performanceSubmittedReports: (sport: string) =>
      `${base}/performance/${sport}/submitted-reports`,
    contestOverview: `${base}/contests`,
    contestSport: (sport: string, category: string = 'all', view?: ContestView) => {
      const slug = toSportSlug(sport);
      const cat = String(category || 'all').toLowerCase();
      const qs = new URLSearchParams();
      if (view && view !== 'browse') qs.set('view', view);
      const query = qs.toString();
      return `${base}/contests/${slug}/${cat}${query ? `?${query}` : ''}`;
    },
  };
};

export const isCoachLearningCoursesPath = (pathname: string) =>
  pathname.startsWith('/coach/learning/courses') || pathname === '/coach/learning';

export const isCoachLearningRoadmapPath = (pathname: string) =>
  pathname.startsWith('/coach/learning/roadmap');

export const isPlayerLearningCoursesPath = (pathname: string) =>
  pathname.startsWith('/player/learning/courses') || pathname === '/player/learning';

export const isPlayerLearningRoadmapPath = (pathname: string) =>
  pathname.startsWith('/player/learning/roadmap');
